import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Optional } from '@vimeo/core/types/generic';

import { IUseBroadcastChannelProps, IUseBroadcastChannelReturn, IChannelMessage } from './types';

export function useBroadcastChannel({
  name,
  isActive = true,
}: IUseBroadcastChannelProps): IUseBroadcastChannelReturn {
  const [isReady, setIsReady] = useState<boolean>(false);
  const broadcastChannelRef = useRef<Optional<BroadcastChannel>>(null);
  const channelSubscriptionsRef = useRef<
    Array<(message: IChannelMessage, event?: MessageEvent<IChannelMessage>) => void>
  >([]);

  /**
   * Check if the BroadcastChannel API is available
   * Needed to prevent errors for cases when storage APIs are blocked in user browser
   * see https://developer.mozilla.org/en-US/docs/Web/Privacy/State_Partitioning
   * @returns boolean
   */
  const isBroadcastChannelAvailable: boolean = useMemo(() => {
    try {
      const channel: BroadcastChannel = new window.BroadcastChannel('test_channel');
      channel.close();
      return true;
    } catch (error) {
      return false;
    }
  }, []);

  const isChannelActive: boolean = useMemo(
    () => Boolean(isActive && isReady && broadcastChannelRef.current),
    [isActive, isReady],
  );

  /**
   * Subscribe to the channel
   * @param subscriber The callback to call when a message is received
   * @returns A function to unsubscribe the callback
   */
  const subscribe = useCallback(
    (subscriber: (message: IChannelMessage, event?: MessageEvent<IChannelMessage>) => void) => {
      /**
       * Add the callback to the subscribers
       */
      channelSubscriptionsRef.current.push(subscriber);

      /**
       * Return a function that unsubscribe the callback
       */
      return () => {
        channelSubscriptionsRef.current.splice(
          channelSubscriptionsRef.current.indexOf(subscriber),
          1,
        );
      };
    },
    [],
  );

  /**
   * Post data to channel
   * @param value The object with data type and payload
   */
  const sendChannelMessage = useCallback(
    (message: IChannelMessage) => {
      if (isReady && broadcastChannelRef.current) {
        /**
         * Post the value to channel
         */
        broadcastChannelRef.current.postMessage(message);

        /**
         * Dispatch the event to the listeners
         */
        channelSubscriptionsRef.current.forEach((subscriber) => subscriber(message));
      }
    },
    [isReady],
  );

  /**
   * Create the broadcast channel
   */
  const createChannel = useCallback(() => {
    /**
     * Check if the BroadcastChannel is supported
     */
    if (
      typeof window !== 'undefined' &&
      'BroadcastChannel' in window &&
      isBroadcastChannelAvailable
    ) {
      /**
       * If the channel does not exist, create a new one
       */
      if (!broadcastChannelRef.current) {
        const broadcastChannel: BroadcastChannel = new window.BroadcastChannel(name);

        /**
         * Subscribe to the message event
         * @param event The message event
         */
        broadcastChannel.onmessage = (event: MessageEvent<IChannelMessage>) => {
          /**
           * Dispatch an event to the listeners
           */
          channelSubscriptionsRef.current.forEach((subscriber) => subscriber(event.data, event));
        };

        /**
         * Subscribe to the message error event
         * @param error
         */
        broadcastChannel.onmessageerror = (error: MessageEvent) => {
          console.error(error);
        };

        broadcastChannelRef.current = broadcastChannel;

        /**
         * Set the channel as ready for rerender purposes
         */
        setIsReady(true);
      }
    } else {
      console.error('BroadcastChannel is not available in this browser');
    }
  }, [name, isBroadcastChannelAvailable]);

  /**
   * Close the broadcast channel and clear subscriptions
   */
  const closeChannel = useCallback(() => {
    if (broadcastChannelRef.current) {
      setIsReady(false);
      broadcastChannelRef.current.close();
      broadcastChannelRef.current = null;
      channelSubscriptionsRef.current = [];
    }
  }, []);

  useEffect(() => {
    /**
     * Create broadcast channel conditionally as replacing for conditional hook call
     */
    if (isActive) {
      createChannel();
    }
  }, [createChannel, isActive]);

  return useMemo(
    () => ({
      isChannelActive,
      broadcastChannel: broadcastChannelRef.current,
      subscribe,
      sendChannelMessage,
      closeChannel,
    }),
    [isChannelActive, subscribe, sendChannelMessage, closeChannel],
  );
}
